import React, { useCallback, useEffect, useRef, useState } from "react";
import MainLayout from "../components/Layout/MainLayout";
import { Breadcrumb, ConfigProvider, Space } from "antd";
import DataTable from "../components/Tables/Datatable";
import { postApi, useApiCall } from "../services/api";
import { Link, useLocation, useOutletContext, useSearchParams } from "react-router-dom";
import FileUploadModal from "../components/models/FileUploadModal";
import { useSelector } from "react-redux";
import { useGlobalModal } from "../context/SuccessModalProvider";
import SearchInput from "../components/Forms/SearchInput";
import Loader from "../common/Loader";
import { roleIds, shirtingCompCodes } from "../constant";
import ProductImageName from "../components/Layout/ProductImageName";
import { calcInStock, getStockStatus, handleRouteNav } from "../utils";
import CustomBadge from "../components/Charts/CustomBadge";
import Filters from "../components/Filters";

const Shades = () => {
  const { postApi, loading } = useApiCall();
  const { showPopup } = useGlobalModal();
  const location = useLocation();
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [allShades, setAllShades] = useState([]);
  const [currentItem, setCurrentItem] = useState(null);
  const user_id = useSelector((state) => state.auth.user_id);
  const full_name = useSelector((state) => state.auth.full_name);
  const { role_id } = useSelector((state) => state.auth);
  const [clientInfo, setClientInfo] = useState({
    short_name: "",
    clienttype: "",
  });
  const { sort_no, company_code } = location.state || {};

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const { appliedFilters, ModalUI, FilterButton } = Filters({
    filters: [
      {
        type: "select",
        key: "status",
        label: "Status",
        inputProps: { placeholder: "Select Status" },
        options: [
          { value: "in stock", label: "In Stock" },
          { value: "make to order", label: "Make to order" },
        ],
      },
    ],
    persistToUrl: false,
    onChange: () => setPagination((prev) => ({ ...prev, current: 1 })),
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const fetchIdRef = useRef(0);

  useEffect(() => {
    if (location.state?.short_name || location.state?.clienttype) {
      setClientInfo({
        short_name: location.state.short_name,
        clienttype: location.state.clienttype,
      });
    }
  }, [location.state]);

  const fetchAllShades = useCallback(async () => {
    fetchIdRef.current += 1;
    const currentFetchId = fetchIdRef.current;
    try {
      let apiEndpoint = "sales/shade/list";
      if (shirtingCompCodes.includes(company_code)) {
        apiEndpoint = "sales/shirting/list";
      }
      const response = await postApi(apiEndpoint, {
        search: query,
        page: pagination.current,
        per_page: pagination.pageSize,
        sort_no,
        filter: appliedFilters?.status,
      });

      if (currentFetchId !== fetchIdRef.current) {
        return;
      }

      setAllShades(response.data || []);

      // update pagination with total records from backend
      setPagination((prev) => ({
        ...prev,
        total: response.total || 0,
      }));
    } catch (error) {
      if (currentFetchId !== fetchIdRef.current) {
        return;
      }
      console.error("Error fetching client details:", error);
    }
  }, [appliedFilters?.status, pagination.current, pagination.pageSize, query, sort_no]);

  useEffect(() => {
    const handler = setTimeout(fetchAllShades, 300);
    return () => clearTimeout(handler);
  }, [fetchAllShades]);

  const handleChange = (paginationInfo, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
    setPagination((prev) => ({
      ...prev,
      current: paginationInfo.pageSize !== prev.pageSize ? 1 : paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    }));
  };

  const onSearch = (value, _e, info) => console.log(info === null || info === void 0 ? void 0 : info.source, value);

  const handleOnSearchChange = (e) => {
    const value = e.target.value;
    setSearchParams(
      (prev) => {
        if (value) prev.set("q", value);
        else prev.delete("q");
        return prev;
      },
      { replace: true, state: location.state },
    );
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleRow = (record) => {
    console.log("Row clicked:", record);
  };

  const cellStyle = {
    padding: "0px",
    textAlign: "center",
    verticalAlign: "middle",
  };

  const columns = [
    {
      title: "#",
      key: "index",
      render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
      width: 15,
      onCell: () => ({ style: cellStyle }),
      onHeaderCell: () => ({ style: cellStyle }),
    },
    {
      title: "Shade",
      dataIndex: "shade",
      key: "shade",
      className: "max-w-[250px]",
      width: 100,
      render: (item, record) => (
        <ProductImageName
          img_url={item?.detail?.shade_picture_url}
          img_alt={record?.descr_shade}
          text={record?.descr_shade}
          btnOnClick={(e) => {
            e.stopPropagation();
            setIsModalOpen(true);
            setCurrentItem(record);
          }}
        />
      ),
    },
    {
      title: "Type",
      dataIndex: "dyed_type",
      key: "dyed_type",
      width: 70,
      render: (value) => <span className="font-manrope font-semibold text-[14px] leading-[120%] tracking-[0%] text-black">{value}</span>,
    },
    {
      title: "Availability",
      dataIndex: "quality_shade",
      key: "quality_shade",
      render: (_, item) => {
        const { key, text, variant } = getStockStatus(item);

        return (
          <div className="w-full flex items-center px-2 py-2">
            <CustomBadge variant={variant} text={text} />
          </div>
        );
      },
      ellipsis: true,
      width: 140,
    },
  ];

  const handleUpload = async (files) => {
    if (!files?.length) {
      showPopup({
        title: null,
        content: <span className="custom-modal-title">Please select an image file to upload.</span>,
        onOk: () => console.log("Confirmed!"),
        className: "custom-success-modal",
      });
      return;
    }

    const file = files[0]?.originFileObj || files[0];
    if (file.type === "image/jpg") file.type = "image/jpeg";

    const validTypes = ["image/jpeg", "image/png"];
    if (!validTypes.includes(file.type)) {
      showPopup({
        title: null,
        content: <span className="custom-modal-title">Please upload only JPG or PNG files.</span>,
        onOk: () => console.log("Confirmed!"),
        className: "custom-success-modal",
      });
      return;
    }

    const isShirting = shirtingCompCodes.includes(currentItem?.company_code);
    const formData = new FormData();
    formData.append("descr", String(currentItem?.descr_shade || ""));
    formData.append("updated_by", String(full_name || ""));
    formData.append("updated_by_id", String(user_id || ""));
    formData.append("shade_picture", file);

    const isUpdate = currentItem?.shade?.detail?.shade_details_id;
    const apiEndpoint = isUpdate
      ? isShirting
        ? "sales/shirting/update"
        : "sales/shade/update"
      : isShirting
        ? "sales/shirting/add"
        : "sales/shade/add";

    if (isUpdate) {
      formData.append("shade_details_id", String(currentItem?.shade?.detail?.shade_details_id));
    } else if (isShirting) {
      formData.append("sort_no", String(currentItem?.sort_no));
      formData.append("descr", String(currentItem?.descr_shade));
      formData.append("dyed_type", String(currentItem?.dyed_type));
      formData.append("short_name", String(currentItem?.short_name));
    }

    try {
      const response = await postApi(apiEndpoint, formData);
      setIsModalOpen(false);

      if (response && response.status) {
        showPopup({
          title: null,
          content: <span className="custom-modal-title">{response.message}</span>,
          onOk: () => console.log("Confirmed!"),
          className: "custom-success-modal",
        });
      }
      fetchAllShades();
    } catch (error) {
      console.error("Upload failed:", error.response?.data || error.message);
    }
  };

  const { setTitle, setDescription, setBackButton } = useOutletContext();

  useEffect(() => {
    setTitle("Shades");
    setDescription(
      <div>
        <Breadcrumb>
          {[
            { title: "Home", href: handleRouteNav("/dashboard", role_id) },
            {
              title: "Product Catalogue ",
              href: handleRouteNav("/product-catalog", role_id),
            },
            {
              title: "View Shades",
              href: handleRouteNav("/product-catalog/shades", role_id),
              state: { sort_no },
            },
          ].map((item, index) => (
            <Breadcrumb.Item key={index}>
              <Link to={item.href} state={item.state}>
                {item.title}
              </Link>
            </Breadcrumb.Item>
          ))}
        </Breadcrumb>
      </div>,
    );
    setBackButton(true);
  }, [setTitle, setDescription, setBackButton]);

  return (
    <>
      <ModalUI />
      <>
        <ConfigProvider
          theme={{
            token: {
              colorPrimary: "#dc2626",
              borderRadius: 8,
            },
            components: {
              Table: {
                headerBg: "#ffffff",
                headerColor: "#6b7280",
                borderColor: "#f3f4f6",
                headerBorderRadius: 0,
              },
            },
          }}
        >
          <div className="mx-auto bg-white rounded-3xl shadow-lg overflow-hidden">
            <div className="px-12 py-6 !pb-0 flex justify-between items-center">
              <div className="text-left">
                <h2 className="text-3xl font-semibold text-gray-800 mb-1 font-abhaya">{clientInfo?.short_name}</h2>
                <p className="text-gray-500 text-xl">{clientInfo?.clienttype}</p>
              </div>
            </div>
            <div className="flex justify-between gap-7 items-center px-8 py-6">
              <SearchInput onSearch={onSearch} value={query} onChange={handleOnSearchChange} placeholder="Search for shades" />
              <FilterButton />
            </div>

            {loading ? (
              <div className="flex justify-center items-center py-20">
                <Loader size={60} />{" "}
              </div>
            ) : (
              <div className="px-6 pb-6 flex-1">
                <DataTable
                  columns={columns}
                  data={allShades}
                  pagination={pagination}
                  handleChange={handleChange}
                  handleRow={handleRow}
                  className="stock-table"
                  disableHover
                />
              </div>
            )}
          </div>
        </ConfigProvider>
      </>
      <FileUploadModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
        onUpload={handleUpload}
        title="Upload Image"
        fileTypes={[".jpg", ".jpeg", ".png"]}
        buttonText="Update Image"
        loading={loading}
        fileTypeLabel="JPG or PNG"
      />
    </>
  );
};

export default Shades;
